import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { dbParaplanning } from "../../../backend/firebaseConfig";
import { getParaplanningChargeItemByIds } from "./paraplanningChargesService";
import { calculateParaplanningCost } from "../utils/calculateParaplanningCost";
import { normaliseMoney } from "../utils/normaliseMoney";
import {
  getMonthKey,
  getQuarterFromDate,
  getQuarterArchiveAfterDate,
  shouldArchiveCase,
} from "../utils/paraplanningDateUtils";

const CASES_PATH = ["Cases", "Dashboard", "Data"];

const casesRef = collection(dbParaplanning, ...CASES_PATH);

const getCaseRef = (caseId) => {
  return doc(dbParaplanning, ...CASES_PATH, caseId);
};

const getSelectedModifiersFromPayload = (payload = {}) => {
  if (Array.isArray(payload.selectedModifiers)) {
    return payload.selectedModifiers;
  }

  if (Array.isArray(payload.chargeSnapshot?.modifiers)) {
    return payload.chargeSnapshot.modifiers;
  }

  return [];
};

const buildFilters = (dateValue) => {
  const filterDate = dateValue || new Date();
  const quarterData = getQuarterFromDate(filterDate);
  const archiveAfter = getQuarterArchiveAfterDate(filterDate);

  return {
    monthKey: getMonthKey(filterDate),
    year: quarterData.year,
    quarter: quarterData.quarter,
    quarterKey: quarterData.quarterKey,
    archiveAfter,
    isArchived: shouldArchiveCase(archiveAfter),
  };
};

const normaliseClients = ({ client = {}, clients = [] }) => {
  if (Array.isArray(clients) && clients.length) {
    return clients.map((item, index) => ({
      id: item.id || `client_${index + 1}`,
      name:
        item.name || `${item.firstName || ""} ${item.lastName || ""}`.trim(),
      firstName: item.firstName || "",
      lastName: item.lastName || "",
      reference: item.reference || "",
      isPrimary: index === 0 || item.isPrimary === true,
    }));
  }

  return [
    {
      id: "primary_client",
      name: client.name || "",
      firstName: client.firstName || "",
      lastName: client.lastName || "",
      reference: client.reference || "",
      isPrimary: true,
    },
  ];
};

const buildCasesQuery = (filters = {}) => {
  const constraints = [orderBy("createdAt", "desc")];

  if (filters.catalogId) {
    constraints.push(where("catalogId", "==", filters.catalogId));
  }

  if (filters.status && filters.status !== "all") {
    constraints.push(where("status", "==", filters.status));
  }

  if (filters.monthKey) {
    constraints.push(where("filters.monthKey", "==", filters.monthKey));
  }

  if (filters.assignedTo) {
    constraints.push(where("assignedTo.email", "==", filters.assignedTo));
  }

  return query(casesRef, ...constraints);
};

const normalise = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase();
};

const toDateKey = (value) => {
  if (!value) return "";

  const date = value?.toDate ? value.toDate() : new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "";
  }

  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

const matchesUserScope = (item = {}, filters = {}) => {
  if (!filters.userEmail || filters.isAdmin || filters.canManageAllCases) {
    return true;
  }

  const userEmail = normalise(filters.userEmail);
  const userName = normalise(filters.userName);

  const assignedEmail = normalise(item?.assignedTo?.email);
  const assignedName = normalise(item?.assignedTo?.name);

  // Pipeline cases are intentionally visible to scoped users so they can pick
  // up unassigned work even when they don't have "view all cases" access.
  const isUnassignedPipelineCase = !assignedEmail && !assignedName;
  if (isUnassignedPipelineCase) {
    return true;
  }

  if (assignedEmail) {
    return assignedEmail === userEmail;
  }

  if (assignedName && userName) {
    return assignedName === userName;
  }

  const createdByEmail = normalise(item?.createdBy?.email);
  if (createdByEmail) {
    return createdByEmail === userEmail;
  }

  const createdByName = normalise(item?.createdBy?.name);
  if (createdByName && userName) {
    return createdByName === userName;
  }

  return false;
};

const mapCasesSnapshot = (snap, filters = {}) => {
  let cases = snap.docs.map((caseDoc) => ({
    id: caseDoc.id,
    ...caseDoc.data(),
  }));

  cases = cases.filter((item) => matchesUserScope(item, filters));

  if (filters.search) {
    const term = String(filters.search).toLowerCase();

    cases = cases.filter((item) =>
      [
        item?.adviser?.name,
        item?.adviser?.email,
        item?.client?.name,
        item?.type,
        item?.assignedTo?.name,
        item?.assignedTo?.email,
      ]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(term)),
    );
  }

  if (filters.startDate) {
    cases = cases.filter((item) => {
      return toDateKey(item?.startDate) === String(filters.startDate || "");
    });
  }

  return cases;
};

export const getParaplanningCases = async (filters = {}) => {
  const q = buildCasesQuery(filters);
  const snap = await getDocs(q);

  return mapCasesSnapshot(snap, filters);
};

export const subscribeParaplanningCases = ({
  filters = {},
  onData,
  onError,
}) => {
  const q = buildCasesQuery(filters);

  return onSnapshot(
    q,
    (snap) => {
      const cases = mapCasesSnapshot(snap, filters);
      onData?.(cases);
    },
    (error) => {
      console.error("[paraplanningCasesService] Live cases error:", error);
      onError?.(error);
    },
  );
};

export const createParaplanningCase = async (payload = {}) => {
  const {
    catalogId,
    categoryId,
    itemId,
    adviser = {},
    client = {},
    clients = [],
    completionDate = "",
    assignedTo = {},
    createdBy = {},
    notes = "",
    tasksForCase = [],
    taskIds = [],
    status = "in_progress",
    highPriority = false,
    startDate = "",
  } = payload;

  if (!catalogId || !categoryId || !itemId) {
    throw new Error("Please select a charge catalogue and case type.");
  }

  const { catalog, category, item } = await getParaplanningChargeItemByIds({
    catalogId,
    categoryId,
    itemId,
  });

  const selectedModifiers = getSelectedModifiersFromPayload(payload);

  const costs = calculateParaplanningCost({
    chargeItem: item,
    selectedModifiers,
  });

  const isCompleted = status === "completed";
  const cleanClients = normaliseClients({ client, clients });
  const primaryClient = cleanClients[0] || {};

  const filterDate = isCompleted
    ? completionDate || startDate || new Date()
    : startDate || new Date();

  const casePayload = {
    catalogId,
    categoryId,
    itemId,

    startDate,

    adviser: {
      name: adviser.name || "",
      email: adviser.email || "",
      adviserId: adviser.adviserId || adviser.email || "",
    },

    client: {
      name: primaryClient.name || "",
      firstName: primaryClient.firstName || "",
      lastName: primaryClient.lastName || "",
      reference: primaryClient.reference || "",
    },

    clients: cleanClients,

    type: payload.type || item.name || "",

    chargeSnapshot: {
      versionId:
        payload.chargeSnapshot?.versionId || catalog.currentVersionId || "",
      catalogName: payload.chargeSnapshot?.catalogName || catalog.name || "",
      categoryName: payload.chargeSnapshot?.categoryName || category.name || "",
      itemName: payload.chargeSnapshot?.itemName || item.name || "",
      itemDescription:
        payload.chargeSnapshot?.itemDescription || item.description || "",
      baseAmount: normaliseMoney(
        payload.chargeSnapshot?.baseAmount ?? payload.baseCharge ?? item.amount,
      ),
      currency: payload.chargeSnapshot?.currency || item.currency || "GBP",
      amountType:
        payload.chargeSnapshot?.amountType || item.amountType || "fixed",
      modifiers: isCompleted ? costs.modifiers : [],
    },

    costs: {
      baseCost: isCompleted
        ? normaliseMoney(
            payload.costs?.baseCost ?? payload.baseCharge ?? costs.baseCost,
          )
        : 0,
      additionalCost: isCompleted
        ? normaliseMoney(payload.costs?.additionalCost ?? costs.additionalCost)
        : 0,
      totalCost: isCompleted
        ? normaliseMoney(
            payload.costs?.totalCost ?? payload.totalCharge ?? costs.totalCost,
          )
        : 0,
    },

    completionDate: isCompleted ? completionDate || startDate || "" : "",
    status: status || "in_progress",
    highPriority: Boolean(highPriority),

    assignedTo: {
      name: assignedTo.name || "",
      email: assignedTo.email || "",
    },

    notes: notes || "",
    tasksForCase: isCompleted ? tasksForCase : [],
    taskIds: isCompleted ? taskIds : [],

    filters: buildFilters(filterDate),

    createdBy: {
      name: createdBy.name || "",
      email: createdBy.email || "",
    },

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    completedAt: isCompleted ? serverTimestamp() : null,
  };

  const docRef = await addDoc(casesRef, casePayload);

  await updateDoc(docRef, {
    id: docRef.id,
    referenceNumber: docRef.id,
  });

  return {
    id: docRef.id,
    referenceNumber: docRef.id,
    ...casePayload,
  };
};

export const updateParaplanningCase = async (caseId, payload = {}) => {
  if (!caseId) {
    throw new Error("Case ID is required.");
  }

  const caseRef = getCaseRef(caseId);
  const caseSnap = await getDoc(caseRef);

  if (!caseSnap.exists()) {
    throw new Error("Case not found.");
  }

  const existingData = caseSnap.data();

  const updatePayload = {
    ...payload,
    createdBy: payload.createdBy || existingData.createdBy || {},
    updatedAt: serverTimestamp(),
  };

  const isCompleted = payload.status === "completed";
  const wasCompleted = existingData.status === "completed";

  const effectiveCompletionDate =
    payload.completionDate ||
    existingData.startDate ||
    existingData.createdAt ||
    "";

  if (isCompleted || payload.completionDate) {
    updatePayload.filters = buildFilters(effectiveCompletionDate);
  }

  if (isCompleted && !wasCompleted) {
    updatePayload.completedAt = serverTimestamp();
  }

  if (payload.status === "in_progress") {
    updatePayload.completedAt = null;
    updatePayload.completionDate = "";
    updatePayload.filters = buildFilters(
      existingData.startDate || existingData.createdAt || "",
    );
  }

  await updateDoc(caseRef, updatePayload);

  return {
    id: caseId,
    ...existingData,
    ...updatePayload,
  };
};

export const completeParaplanningCase = async (caseId, completedBy = {}) => {
  if (!caseId) {
    throw new Error("Case ID is required.");
  }

  const caseRef = getCaseRef(caseId);
  const caseSnap = await getDoc(caseRef);

  if (!caseSnap.exists()) {
    throw new Error("Case not found.");
  }

  const today = new Date().toISOString().split("T")[0];

  const user = {
    name: completedBy.name || "",
    email: completedBy.email || "",
  };

  const updatePayload = {
    status: "completed",
    completionDate: today,
    filters: buildFilters(today),
    completedAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  if (user.name || user.email) {
    updatePayload.completedBy = user;
    updatePayload.lastEditedBy = user;
  }

  await updateDoc(caseRef, updatePayload);

  return {
    id: caseId,
    ...caseSnap.data(),
    ...updatePayload,
    status: "completed",
    completionDate: today,
  };
};

export const deleteParaplanningCase = async (caseId) => {
  if (!caseId) {
    throw new Error("Case ID is required.");
  }

  const caseRef = getCaseRef(caseId);

  await deleteDoc(caseRef);

  return {
    success: true,
  };
};

export const markParaplanningCaseAsNpw = async (caseId, markedBy = {}) => {
  if (!caseId) {
    throw new Error("Case ID is required.");
  }

  const caseRef = getCaseRef(caseId);
  const caseSnap = await getDoc(caseRef);

  if (!caseSnap.exists()) {
    throw new Error("Case not found.");
  }

  const today = new Date().toISOString().split("T")[0];

  const user = {
    name: markedBy.name || "",
    email: markedBy.email || "",
  };

  const updatePayload = {
    status: "npw",
    npwDate: today,
    npwAt: serverTimestamp(),
    completionDate: "",
    completedAt: null,
    updatedAt: serverTimestamp(),
  };

  if (user.name || user.email) {
    updatePayload.npwBy = user;
    updatePayload.lastEditedBy = user;
  }

  await updateDoc(caseRef, updatePayload);

  return {
    id: caseId,
    ...caseSnap.data(),
    ...updatePayload,
    status: "npw",
    npwDate: today,
  };
};